import * as React from 'react';
import { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
// import moment from 'moment';

// components and helpers
import { ColorModeContext } from '../../App';
import lightIcon from '../../assets/img/light_mode_icon.svg';
import darkIcon from '../../assets/img/dark_mode_icon.svg';
import { AppContext } from '../../containers/AppProviderWrapper';
import logo from '../../assets/img/hde_logo.svg';
import logo_dark from '../../assets/img/logo_dark_mode.svg';
import Contact from '../Contact/Contact';

// mui
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Fab from '@mui/material/Fab';
import CommentIcon from '@mui/icons-material/Comment';
import Popover from '@mui/material/Popover';
// import { Chip, Stack } from '@mui/material';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';

const drawerWidth = 240;

export const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

export const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

// @ts-ignore
const CustomGridContainer = styled(Grid)(({ theme, open }) => ({
    width: 'calc(100vw - 150px)',
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: 'calc(100vw - 275px)',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

// @ts-ignore
const Navbar = ({ children }) => {
    const theme = useTheme();
    const { logout, user } = useAuth0();
    const {
        handleCloseContact,
        handleClickOpenContact,
        contactAnchorEl,
        contactRef,
    } = useContext(AppContext);
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const colorMode = useContext(ColorModeContext);
    const currentUserPicture = useSelector(
        (state: any) => state.user.currentUserPicture
    );
    const currentUser = useSelector((state: any) => state.user.currentUser);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleLogout = () => {
        // @ts-ignore
        logout({ returnTo: window.location.origin });
    };

    const handleOpenProfile = () => {
        navigate('/account');
        handleCloseUserMenu();
    };

    // const handleTrialClick = () => {
    //     navigate('/account?id=billing');
    // };

    const contactOpen = Boolean(contactAnchorEl);
    const id = contactOpen ? 'simple-popover' : undefined;
    // const daysRemaining =
    //     currentUser &&
    //     currentUser.userConfiguration &&
    //     currentUser.userConfiguration.expirationDate &&
    //     moment(currentUser.userConfiguration.expirationDate).diff(
    //         moment(),
    //         'days'
    //     );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open}>
                <Toolbar>
                    <Box sx={{ flexGrow: 1, display: { xs: 'flex' } }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{
                                marginRight: 5,
                                ...(open && { display: 'none' }),
                                color: '#808080',
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Link to={'/'}>
                            <img
                                src={
                                    theme.palette.mode === 'light'
                                        ? logo
                                        : logo_dark
                                }
                                alt=""
                                height={45}
                            />
                        </Link>
                    </Box>
                    {/*{currentUser &&*/}
                    {/*    currentUser.userConfiguration &&*/}
                    {/*    currentUser.userConfiguration.beta && (*/}
                    {/*        <Box sx={{ paddingRight: 3 }}>*/}
                    {/*            <Grid item>*/}
                    {/*                <Stack*/}
                    {/*                    direction="column"*/}
                    {/*                    justifyContent={'center'}*/}
                    {/*                    alignItems={'center'}*/}
                    {/*                >*/}
                    {/*                    <Chip*/}
                    {/*                        label="Free Trial"*/}
                    {/*                        sx={{*/}
                    {/*                            color: '#fff',*/}
                    {/*                            background:*/}
                    {/*                                'linear-gradient(90deg, #395694 0%, #5F3994 95.31%)',*/}
                    {/*                            fontWeight: 'bold',*/}
                    {/*                        }}*/}
                    {/*                        onClick={handleTrialClick}*/}
                    {/*                    />*/}
                    {/*                    {theme.palette.mode === 'light' &&*/}
                    {/*                    daysRemaining >= 7 ? (*/}
                    {/*                        <Typography*/}
                    {/*                            variant={'caption'}*/}
                    {/*                            style={{*/}
                    {/*                                background:*/}
                    {/*                                    '-webkit-linear-gradient(90deg, #395694 0%, #5F3994 95.31%)',*/}
                    {/*                                // @ts-ignore*/}
                    {/*                                '-webkit-background-clip':*/}
                    {/*                                    'text',*/}
                    {/*                                '-webkit-text-fill-color':*/}
                    {/*                                    'transparent',*/}
                    {/*                            }}*/}
                    {/*                        >*/}
                    {/*                            {daysRemaining} Days Remaining*/}
                    {/*                        </Typography>*/}
                    {/*                    ) : (*/}
                    {/*                        <Typography*/}
                    {/*                            variant={'caption'}*/}
                    {/*                            style={{*/}
                    {/*                                color:*/}
                    {/*                                    daysRemaining < 7*/}
                    {/*                                        ? theme.palette*/}
                    {/*                                              .mode ===*/}
                    {/*                                          'dark'*/}
                    {/*                                            ? '#FF4242'*/}
                    {/*                                            : '#B30000'*/}
                    {/*                                        : '#fff',*/}
                    {/*                            }}*/}
                    {/*                        >*/}
                    {/*                            {daysRemaining > 0*/}
                    {/*                                ? `${daysRemaining} Days Remaining`*/}
                    {/*                                : 'Expired'}*/}
                    {/*                        </Typography>*/}
                    {/*                    )}*/}
                    {/*                </Stack>*/}
                    {/*            </Grid>*/}
                    {/*        </Box>*/}
                    {/*    )}*/}
                    {currentUser &&
                        currentUser.userConfiguration &&
                        currentUser.userConfiguration.beta && (
                            <Box
                                sx={{
                                    height: 60,
                                    paddingRight: 3,
                                    lexGrow: 1,
                                    display: { xs: 'flex' },
                                }}
                            >
                                <Divider
                                    orientation="vertical"
                                    variant={'middle'}
                                    style={{
                                        // backgroundColor: '#CAD2E4',
                                        backgroundColor: '#5F3994',
                                        height: 'auto',
                                        width: 1,
                                    }}
                                />
                            </Box>
                        )}
                    <Box sx={{ paddingRight: 3 }}>
                        <IconButton
                            onClick={colorMode.toggleColorMode}
                            color="inherit"
                            disableRipple
                        >
                            {theme.palette.mode === 'dark' ? (
                                <img src={lightIcon} alt="" />
                            ) : (
                                <img src={darkIcon} alt="" />
                            )}
                        </IconButton>
                    </Box>
                    <Box sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="Open settings">
                            <IconButton
                                onClick={handleOpenUserMenu}
                                sx={{ p: 0 }}
                            >
                                <Avatar
                                    alt={user?.nickname}
                                    src={currentUserPicture}
                                />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            {['My Account', 'Logout'].map((setting) => (
                                <MenuItem
                                    key={setting}
                                    onClick={
                                        setting === 'Logout'
                                            ? handleLogout
                                            : handleOpenProfile
                                    }
                                >
                                    <Typography textAlign="center">
                                        {setting}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open}>
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'rtl' ? (
                            <ChevronRightIcon />
                        ) : (
                            <ChevronLeftIcon />
                        )}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <Grid
                    container
                    direction={'column'}
                    justifyContent={'space-between'}
                    sx={{ height: '100vh' }}
                >
                    <Grid item>
                        <List>
                            {['Home'].map((text, index) => (
                                <ListItem
                                    key={text}
                                    disablePadding
                                    sx={{ display: 'block' }}
                                    component={Link}
                                    to="/"
                                >
                                    <ListItemButton
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open
                                                ? 'initial'
                                                : 'center',
                                            px: 2.5,
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 3 : 'auto',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {<HomeRoundedIcon />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={text}
                                            sx={{
                                                opacity: open ? 1 : 0,
                                                color: '#808080',
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <Grid item xs={12}>
                            <Divider sx={{ margin: 1 }} />
                        </Grid>
                        <List>
                            {['Explorer Library'].map((text, index) => (
                                <ListItem
                                    key={text}
                                    disablePadding
                                    sx={{ display: 'block' }}
                                    component={Link}
                                    to="/explorer-library"
                                >
                                    <ListItemButton
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open
                                                ? 'initial'
                                                : 'center',
                                            px: 2.5,
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 3 : 'auto',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {<AnalyticsIcon />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={text}
                                            sx={{
                                                opacity: open ? 1 : 0,
                                                color: '#808080',
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                    <Grid item sx={{ mb: 1 }}>
                        <List>
                            <ListItem
                                key={'help'}
                                disablePadding
                                sx={{ display: 'block' }}
                                component={Link}
                                target={'_blank'}
                                to="https://acoer.atlassian.net/wiki/spaces/HDEUG/overview"
                            >
                                <ListItemButton
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open
                                            ? 'initial'
                                            : 'center',
                                        px: 2.5,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {<HelpRoundedIcon />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={'Help Center'}
                                        sx={{
                                            opacity: open ? 1 : 0,
                                            color: '#808080',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </List>
                        <Typography
                            variant={'caption'}
                            sx={{ ml: 2 }}
                            color={'#999ba8'}
                        >
                            v{process.env.REACT_APP_VERSION}
                        </Typography>
                    </Grid>
                </Grid>
            </Drawer>
            <Box component="main" sx={{ p: 3 }}>
                <DrawerHeader />
                {/*@ts-ignore*/}
                <CustomGridContainer open={open}>
                    {children}
                </CustomGridContainer>
            </Box>
            <Grid item xs={12}>
                <Tooltip title={'Contact Us'}>
                    <Fab
                        size="small"
                        color="primary"
                        aria-label="add"
                        onClick={handleClickOpenContact}
                        ref={contactRef}
                        sx={{
                            position: 'fixed',
                            bottom: (theme) => theme.spacing(2),
                            right: (theme) => theme.spacing(2),
                            color: '#fff',
                            backgroundColor: '#325599',
                        }}
                    >
                        <CommentIcon />
                    </Fab>
                </Tooltip>
            </Grid>
            <Popover
                id={id}
                open={contactOpen}
                anchorEl={contactAnchorEl}
                onClose={handleCloseContact}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                PaperProps={{
                    style: {
                        borderRadius: 15,
                    },
                }}
            >
                <Contact width={350} />
            </Popover>
        </Box>
    );
};

export default Navbar;
